import { useState, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import withPermissionProtection from 'hoc/withPermissionProtection';

import Grid from '@material-ui/core/Grid';
import { Tabs, Tab } from 'components/CncUI/Tabs';
import Typography from 'components/CncUI/Typography';
import Controls from './Controls';
import CognitoLogs from './CognitoLogs';
import TruliooLogs from './TruliooLogs';
import LexisLogs from './LexisLogs';

import SnackbarContext from 'context/Snackbar/Context';
import useQuery from 'hooks/useQuery';
import useFeatureFlags from 'hooks/useFeatureFlags';
import isEmail from 'utilities/validateEmail';
import isTaxId from 'utilities/validateFEIN';
import TABS from './tabs';

const useStyles = makeStyles(({ palette, breakpoints }) => ({
  header: {
    marginTop: 40,
    marginBottom: 0,
    [breakpoints.up('lg')]: {
      fontSize: 48,
      lineHeight: '56px',
    },
  },
  link: {
    color: palette.cncUI.misc.link,
  },
}));

const LOANOUTS_ENABLED = 'MyCnC-Loanout-Registration';

const IdLogs = () => {
  const classes = useStyles();
  const { isEnabled } = useFeatureFlags();
  const notify = useContext(SnackbarContext);
  const getUserIdentities = useQuery('getUserIdentities', {
    scopes: 'employee_registrations:read',
    fetchPolicy: 'network-only',
    request: (lastFourSsn, email) => ({ lastFourSsn, email }),
    response: res => res.data.getUserIdentities,
  });
  const getTruliooLogs = useQuery('getTruliooLogs', {
    scopes: 'employee_registrations:read',
    fetchPolicy: 'network-only',
    request: (lastFourSsn, email) => ({ lastFourSsn, email }),
    response: res => res.data.getTruliooLogs,
  });
  const getLexisLogs = useQuery('getLexisLogs', {
    scopes: 'employee_registrations:read',
    fetchPolicy: 'network-only',
    request: (taxId, companyName) => ({ taxId, companyName }),
    response: res => {
      let rows = (res.data.getLexisLogs || []).map(r => ({
        ...r,
        input: JSON.parse(r.input),
        data: JSON.parse(r.data),
      }));
      return rows;
    },
  });
  const query = {
    [TABS.COGNITO]: getUserIdentities,
    [TABS.TRULIOO]: getTruliooLogs,
    [TABS.LEXIS]: getLexisLogs,
  };

  const [state, setState] = useState({
    loading: '',
    records: [],
  });
  const modify = data => setState(old => ({ ...old, ...data }));
  const [tab, setTab] = useState(0);
  const handleChange = (_, index) => {
    clear();
    setTab(index);
  };

  const search = value => {
    modify({ loading: true });
    let searchValue1, searchValue2;
    if (tab === TABS.LEXIS) {
      // For Lexis query
      // taxId
      searchValue1 = isTaxId(value) ? value : '';
      // companyName
      searchValue2 = !isTaxId(value) ? value : '';
    } else {
      // For Cognito and Trulioo query
      // lastFourSsn
      searchValue1 = !isEmail(value) ? value : '';
      // email
      searchValue2 = isEmail(value) ? value : '';
    }
    query[tab](searchValue1, searchValue2)
      .then(records => modify({ records }))
      .catch(err => {
        notify({
          show: true,
          type: 'error',
          title: 'Error ocurred',
          message: err.message,
        });
      })
      .finally(() => modify({ loading: false }));
  };

  const clear = () => {
    setState({ loading: '', records: [] });
  };

  const instructions = {
    [TABS.COGNITO]: (
      <>
        To retrieve the <strong>Cognito</strong> log, you only require the last
        four digits of the user’s SSN or Email.
      </>
    ),
    [TABS.TRULIOO]: (
      <>
        To retrieve the <strong>Trulioo</strong> log, you only require the last
        four digits of the user’s SIN or Email.
      </>
    ),
    [TABS.LEXIS]: (
      <>
        To retrieve the <strong>LexisNexis</strong> log, you require the
        corporation’s tax-id or business name.
      </>
    ),
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} id="HeaderTitle">
        <Typography variant="h2" className={classes.header}>
          ID Verification Log Viewer
        </Typography>
      </Grid>
      <Grid item xs={12}>
        {/* Tab System */}
        <Tabs value={tab} onChange={handleChange}>
          <Tab>Cognito Logs</Tab>
          <Tab>Trulioo Logs</Tab>
          {isEnabled(LOANOUTS_ENABLED) && <Tab>LexisNexis Logs</Tab>}
        </Tabs>
      </Grid>
      <Grid item xs={12}>
        <Typography>{instructions[tab]}</Typography>
      </Grid>
      <Grid item xs={12}>
        <Controls
          currentTab={tab}
          onSearch={search}
          loading={state.loading}
          clear={clear}
        />
      </Grid>
      <Grid item xs={12}>
        {tab === TABS.COGNITO && <CognitoLogs identities={state.records} />}
        {tab === TABS.TRULIOO && <TruliooLogs records={state.records} />}
        {isEnabled(LOANOUTS_ENABLED) && tab === TABS.LEXIS && (
          <LexisLogs records={state.records} />
        )}
      </Grid>
    </Grid>
  );
};

export default withPermissionProtection('employee_registrations:read')(IdLogs);
